import React from 'react';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell
} from "recharts";
import { ChartCard, CustomProgressBar } from './Shared/AnalyticsComponents';

const COLORS = ['#FF5526', '#FF5526', '#FF5526', '#059669', '#D97706', '#DC2626'];

const ChartsGrid = ({ trends }) => {
    const winRateData = trends?.win_rate_trend?.map(d => ({ month: d.period, rate: d.rate })) || [];
    const pipelineData = trends?.pipeline_vs_won?.map(d => ({ month: d.period, value: d.pipeline, won: d.won })) || [];
    const responseTimeData = trends?.response_time?.map(d => ({ month: d.period, days: d.days })) || [];
    const proposalVolumeData = trends?.proposal_volume?.map(d => ({ month: d.period, won: d.won, lost: d.lost })) || [];
    const industryBreakdown = trends?.industry_breakdown?.map(d => ({ name: d.label, value: d.value })) || [];
    const lossReasons = trends?.loss_reasons?.map(d => ({ reason: d.reason, pct: d.pct })) || [];

    return (
        <>
            <div className="main-charts-row">
                <ChartCard title="Win Rate Trend">
                    <ResponsiveContainer width="100%" height={190}>
                        <AreaChart data={winRateData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                            <defs>
                                <linearGradient id="winGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#FF5526" stopOpacity={0.15} />
                                    <stop offset="95%" stopColor="#FF5526" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                            <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} dy={8} />
                            <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 10 }} />
                            <Area type="monotone" dataKey="rate" stroke="#FF5526" strokeWidth={2.5} fill="url(#winGrad)" dot={{ r: 3, fill: "#FF5526" }} />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Pipeline vs Won ($M)">
                    <ResponsiveContainer width="100%" height={190}>
                        <BarChart data={pipelineData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                            <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} dy={8} />
                            <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 10 }} />
                            <Bar dataKey="value" fill="#FFF1EC" radius={[3, 3, 0, 0]} name="Pipeline" barSize={10} />
                            <Bar dataKey="won" fill="#FF5526" radius={[3, 3, 0, 0]} name="Won" barSize={10} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Response Time (days)">
                    <ResponsiveContainer width="100%" height={190}>
                        <LineChart data={responseTimeData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                            <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} dy={8} />
                            <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 10 }} />
                            <Line type="monotone" dataKey="days" stroke="#10B981" strokeWidth={2.5} dot={{ fill: "#10B981", r: 3 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            <div className="main-charts-row">
                <ChartCard title="Proposal Volume">
                    <ResponsiveContainer width="100%" height={190}>
                        <BarChart data={proposalVolumeData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                            <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} dy={8} />
                            <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 10 }} />
                            <Bar dataKey="won" stackId="a" fill="#10B981" barSize={16} />
                            <Bar dataKey="lost" stackId="a" fill="#FCA5A5" barSize={16} radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Industry Breakdown" className="industry-breakdown-card">
                    <div className="pie-chart-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '1rem', height: '190px' }}>
                        <div style={{ position: 'relative', width: 120, height: 120 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={industryBreakdown} cx="50%" cy="50%" innerRadius={38} outerRadius={58} paddingAngle={4} dataKey="value" strokeWidth={0}>
                                        {industryBreakdown.map((entry, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                <span style={{ fontSize: '1rem', fontWeight: 800, color: '#0F172A' }}>100%</span>
                                <span style={{ fontSize: '0.55rem', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase' }}>Total</span>
                            </div>
                        </div>
                        <div className="pie-labels-list">
                            {industryBreakdown.map((item, i) => (
                                <div key={item.name} className="pie-label-item">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                        <div className="pie-dot" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                                        <span style={{ color: '#475569', fontSize: '0.75rem' }}>{item.name}</span>
                                    </div>
                                    <span style={{ color: '#1E293B', fontSize: '0.75rem' }}>{item.value}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </ChartCard>

                <ChartCard title="Loss Reasons">
                    <div className="loss-reasons-list">
                        {lossReasons.length > 0 ? lossReasons.map((r) => (
                            <div key={r.reason} className="loss-reason-item">
                                <div className="loss-reason-info" style={{ fontSize: '0.75rem' }}>
                                    <span className="loss-label">{r.reason}</span>
                                    <span className="loss-value">{r.pct}%</span>
                                </div>
                                <CustomProgressBar value={r.pct} color={r.pct > 25 ? "#F87171" : r.pct > 15 ? "#E9471D" : "#CBD5E1"} />
                            </div>
                        )) : <div className="text-center py-6 text-slate-400 text-xs">No data available</div>}
                    </div>
                </ChartCard>
            </div>
        </>
    );
};

export default ChartsGrid;
